// @flow strict

import React, { type Node } from 'react';
import RehypeReact from 'rehype-react';

import { graphql, Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';

import { Box, Grid } from '@material-ui/core';
import ArrowIcon from '@material-ui/icons/KeyboardArrowLeftOutlined';

import { Layout } from '~components/layout/layout-glossary';
import { Author } from '~components/author/author';
import { Blockquote } from '~components/markdown/blockquote/blockquote';
import { BulletHeadline } from '~components/markdown/bullet-headline/bullet-headline';
import { Button } from '~components/markdown/button/button';
import {
  BulletList,
  BulletListItem,
} from '~components/markdown/bullet-list/bullet-list';
import { FAQ } from '~components/markdown/faq/faq';
import { Note } from '~components/markdown/note/note';
import { Video } from '~components/markdown/video/video';
import { Audio } from '~components/markdown/audio/audio';
import { YoutubePlayer } from '~components/markdown/youtube/youtube-player';
import { NewsletterSignup } from '~components/newsletter-signup/newsletter-signup';
import { PostPreview } from '~components/blog-elements/post-preview';
import { Sidebar } from '~components/sidebar/sidebar';
import { Tags } from '~components/tags/tags';

import { createLinkPath } from '~utils/helpers/create-link-path';
import { useLanguageContext } from '~contexts/language-context';
import { type AppHOCType, withApp } from '../app';

import type { PostNode } from '~types/post';

import * as styles from './glossary-post.module.less';
import { ArrowBack } from '@material-ui/icons';

type BlogPostProps = {
  data: {
    markdownRemark: PostNode,
    allMarkdownRemark: {
      nodes: Array<PostNode>,
    },
    site: {
      siteMetadata: { domain: string },
    },
  },

  pageContext: {
    canonical: string,
    faq?: Array<{ question: string, answer: string }>,
  },
};

function renderRelatedArticles(articles): Node {
  if (articles == null || articles.length === 0) {
    return null;
  }

  return (
    <section>
      <Grid container justifyContent="center" spacing={4}>
        <Grid item md={10}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6}>
              <PostPreview post={articles[0]} />
            </Grid>

            {articles[1] && (
              <Box
                component={Grid}
                item
                sm={6}
                display={{ xs: 'none', sm: 'block' }}
              >
                <PostPreview post={articles[1]} />
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>
    </section>
  );
}

const renderAst = new RehypeReact({
  createElement: React.createElement,
  components: {
    'omq-youtube': YoutubePlayer,
    'omq-button': Button,
    'omq-video': Video,
    'omq-audio': Audio,
    'omq-bullet-list': BulletList,
    'omq-bullet-list-item': BulletListItem,
    'omq-bullet-headline': BulletHeadline,
    'omq-blockquote': Blockquote,
    'omq-note': Note,
    'omq-faq': FAQ,
  },
}).Compiler;

const createJsonLdVideoObject = (
  htmlAstVideoElement,
  domain,
  datePublished,
  frontmatter,
) => {
  if (!htmlAstVideoElement?.tagName) {
    return null;
  }

  const uploadDate = htmlAstVideoElement?.properties?.uploaddate
    ? htmlAstVideoElement?.properties?.uploaddate + '.000Z'
    : datePublished;

  if (htmlAstVideoElement?.tagName === 'omq-video') {
    return {
      '@type': 'VideoObject',
      name: htmlAstVideoElement?.properties?.title ?? frontmatter.title,
      description:
        htmlAstVideoElement?.properties?.description ?? frontmatter.description,
      thumbnailUrl: `${domain}${htmlAstVideoElement.properties.image}`,
      contentUrl: `${domain}${htmlAstVideoElement.properties.src}`,
      uploadDate: uploadDate,
    };
  }

  if (htmlAstVideoElement?.tagName === 'omq-youtube') {
    return {
      '@type': 'VideoObject',
      name: htmlAstVideoElement?.properties?.title ?? frontmatter.title,
      description:
        htmlAstVideoElement?.properties?.description ?? frontmatter.description,
      thumbnailUrl: `https://img.youtube.com/vi/${htmlAstVideoElement.properties.id}/0.jpg`,
      contentUrl: `https://www.youtube.com/embed/${htmlAstVideoElement.properties.id}`,
      uploadDate: uploadDate,
    };
  }
};

const findFirstVideoInHtmlAst = (htmlAst) => {
  const video = {};
  const ast = htmlAst?.children ?? [];

  if (ast.length === 0) {
    return {};
  }

  const potentialVideoElements = ast.filter(
    (element) => element.tagName === 'div',
  );

  potentialVideoElements.forEach((element) => {
    const potentialVideoElement = element?.children[0] ?? {};
    const tagName = potentialVideoElement?.tagName ?? '';
    if (tagName === 'omq-video' || tagName === 'omq-youtube') {
      Object.assign(video, potentialVideoElement);
    }
  });

  return video;
};

const BlogPostTemplate = ({ data, pageContext }: BlogPostProps) => {
  const post = data.markdownRemark;

  const relatedArticles = data.allMarkdownRemark.nodes;
  const domain = data.site.siteMetadata.domain;

  const { frontmatter, fields, timeToRead } = post;

  const { image, alternates, language, permalink } = frontmatter;
  if (image == null) {
    console.error('blog post does not have an image', frontmatter.title);
  }

  const { loc } = useLanguageContext();

  const imageData = image.childImageSharp.gatsbyImageData;
  const relatedArticlesContent = renderRelatedArticles(relatedArticles);

  const alt = (alternates || []).reduce((result, current) => {
    result[current.lang] = current.href;
    return result;
  }, {});

  alt[language] = `${permalink}`;
  alt.fallback = {
    de: '/de/blog/',
    en: '/blog/',
  };

  const metaTags = [
    {
      name: 'author',
      content: fields?.author?.name ?? 'OMQ Team',
    },
    {
      property: 'og:url',
      content: createLinkPath({
        path: permalink,
        domain,
      }),
    },
    {
      name: 'image',
      property: 'og:image',
      content: createLinkPath({
        path: imageData.images.fallback.src,
        domain,
        ignoreTrailingSlash: true,
      }),
    },
    {
      name: 'twitter:image',
      content: createLinkPath({
        path: imageData.images.fallback.src,
        domain,
        ignoreTrailingSlash: true,
      }),
    },
  ];

  const ldJson = {
    author: fields?.author?.name ?? 'OMQ Team',
    url: permalink,
    datePublished: pageContext.datePublishedTimestamp,
    image: imageData.images.fallback.src,
    video: createJsonLdVideoObject(
      findFirstVideoInHtmlAst(post.htmlAst),
      domain,
      pageContext.datePublishedTimestamp,
      frontmatter,
    ),
  };

  const patchHTMLAST = (ast) => {
    // see for more information: https://github.com/gatsbyjs/gatsby/issues/34338
    // There is a funny bug somewhere in one of the dozens-hundreds of libraries being used to transform markdown which
    // is causing a problem with `srcset`.  Images are being converted into HTML in the markdown which automatically
    // references the sources of the generated resized/converted images.  The generated `srcset` prop is being somehow
    // converted into an array of strings before being passed to `rehype-react` which is then concatenating them and
    // generating invalid HTML.
    //
    // This code handles converting `srcset` arrays into valid strings.
    if (ast.properties?.srcSet && Array.isArray(ast.properties.srcSet)) {
      ast.properties.srcSet = ast.properties.srcSet.join(', ');
    }
    if (ast.children) {
      ast.children = ast.children.map(patchHTMLAST);
    }
    return ast;
  };

  return (
    <Layout
      seoFields={{
        title: frontmatter?.meta?.title
          ? frontmatter.meta.title
          : frontmatter.title,
        description: frontmatter?.meta?.description
          ? frontmatter.meta.description
          : frontmatter.description,
        language: frontmatter.language,
        canonical: createLinkPath({
          path: pageContext.canonical,
          domain,
          isAbsolute: true,
        }),
        alternates: alt,
        meta: metaTags,
        ldJson,
        faqLdJson: pageContext.faq,
      }}
      alternates={alt}
    >
      <article className={styles.blogPostArticle}>
        <Grid container justifyContent="center" spacing={4}>
          <Grid item xs={12} md={10} zeroMinWidth>
            <header className={styles.header}>
              <Link
                className={styles.linkToLexicon}
                to={language === 'en' ? '/lexicon/' : '/de/lexikon/'}
              >
                <ArrowIcon className={styles.linkToLexicon__arrowIcon} />
                {loc('_glossary_back_to')}
              </Link>
              <p className={styles.category}>{frontmatter.category}</p>
              <h1 className={styles.title}>{frontmatter.title}</h1>
              <p className={styles.description}>{frontmatter.description}</p>

              <div className={styles.authorMobile}>
                <Author
                  size="large"
                  authors={fields.authors}
                  date={frontmatter.date}
                  dateRaw={frontmatter.dateRaw}
                  updated={frontmatter.updated}
                  updatedRaw={frontmatter.updatedRaw}
                  timeToRead={timeToRead}
                />
              </div>

              <figure>
                <GatsbyImage
                  image={imageData}
                  className={styles.image}
                  loading="eager"
                  alt="Blog post thumbnail"
                />
              </figure>

              <div className={styles.authorDesktop}>
                <Author
                  size="large"
                  authors={fields.authors}
                  date={frontmatter.date}
                  dateRaw={frontmatter.dateRaw}
                  updated={frontmatter.updated}
                  updatedRaw={frontmatter.updatedRaw}
                  timeToRead={timeToRead}
                />
              </div>
            </header>
          </Grid>
          <Grid item xs={12} md={7} zeroMinWidth>
            <Sidebar post={frontmatter} />

            <section className={styles.section}>
              {renderAst(patchHTMLAST(post.htmlAst))}
            </section>

            <Tags tags={frontmatter.tags} />
          </Grid>
        </Grid>
      </article>

      {relatedArticlesContent}

      <NewsletterSignup />
    </Layout>
  );
};

const BlogPostApp: AppHOCType<BlogPostProps> =
  withApp<BlogPostProps>(BlogPostTemplate);
export default BlogPostApp;

// noinspection JSUnusedGlobalSymbols
export const pageQuery: string = graphql`
  query GlossaryPostByPermalink(
    $permalink: String!
    $language: String!
    $related_articles: [String!]
    $dateFormat: String!
  ) {
    allMarkdownRemark(
      filter: {
        frontmatter: {
          title: { in: $related_articles }
          language: { eq: $language }
        }
        fields: { draft: { eq: false } }
      }
      sort: { fields: [frontmatter___date], order: DESC }
      limit: 2
    ) {
      nodes {
        ...PostPreview
      }
    }

    markdownRemark(
      frontmatter: {
        permalink: { eq: $permalink }
        language: { eq: $language }
      }
    ) {
      ...Post
    }

    site {
      siteMetadata {
        domain
      }
    }
  }
`;
